import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import {
    View,
    ViewDensity,
    ViewOptions,
    ViewType,
} from "@common/types/views/views";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    DEFAULT_CATALOG_ITEM_ACCOUNT_SOURCES,
    DEFAULT_CATALOG_ITEM_SORT,
    CatalogItemArchivedFilter,
    CatalogItemSort,
} from "@common/types/catalogItem";
import { InvoiceSource } from "@common/types/invoice";
import {
    CatalogItemView,
    CatalogItemViewSchema,
    CatalogItemColumn,
} from "@common/types/views/catalogItemViews";
import { ClassFilterType } from "@common/types/class";
import { useEntities } from "@hooks/useEntities";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import {
    useDeleteCatalogItem,
    useDuplicateCatalogItem,
    useCatalogItems,
    useUpdateCatalogItem,
} from "@api/catalogItem.api";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { FiltersChangeFn } from "@components/pageHeader/filters/lib";
import { scopeAccountIdsToEntities } from "@components/pageHeader/filters/Tabs/CatalogItemLedgerAccountFilter/catalogItemLedgerAccountSelection";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { CatalogItemsListHeader } from "./CatalogItemsListHeader";
import { CatalogItemsListFooter } from "./CatalogItemsListFooter";
import { CatalogItemFormModal } from "./CatalogItemFormModal";
import { CatalogItemsEmptyView } from "./CatalogItemsEmptyView";
import { useCatalogItemBulkActions } from "./useCatalogItemBulkActions";
import { useCatalogItemRowSelection } from "./useCatalogItemRowSelection";
import { createCatalogItemViewConfig } from "./catalogItemViewConfig";
import {
    getGatedCatalogItemColumns,
    restoreGatedCatalogItemColumns,
    stripGatedCatalogItemColumns,
} from "./catalogItemGatedColumns";
import {
    CatalogItemFilters,
    CatalogItemRow,
    CatalogItemsTableCallbacks,
    CatalogItemsTableMeta,
    buildCatalogItemRows,
    resolveCatalogItemClassFilterType,
} from "./catalogItems.types";

const ITEMS_PAGE_SIZE = 50;

const BULK_SELECTION_VIEW_OPTIONS = {
    selection: true,
    bulkActions: true,
} satisfies ViewOptions;

const buildDefaultView = (hasClassesAccess: boolean): CatalogItemView => ({
    type: ViewType.CATALOG_ITEM,
    sort: DEFAULT_CATALOG_ITEM_SORT,
    columns: [
        { id: CatalogItemColumn.NAME, options: { pin: "left" } },
        { id: CatalogItemColumn.DESCRIPTION },
        { id: CatalogItemColumn.UNIT_PRICE },
        // The Class column is a CLASSES-plan feature: omit it from the default
        // columns entirely for workspaces without access (mirrors the invoice
        // by-line-item view). It stays hidden from Column Settings via the
        // `internal` flag in catalogItemViewConfig.
        ...(hasClassesAccess ? [{ id: CatalogItemColumn.CLASS }] : []),
        { id: CatalogItemColumn.LAST_UPDATED },
        { id: CatalogItemColumn.ENTITY },
        { id: CatalogItemColumn.INVOICE_COUNT },
        { id: CatalogItemColumn.TEMPLATE_COUNT },
        { id: CatalogItemColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: { density: ViewDensity.MD },
});

export const CatalogItemsList: React.FC = () => {
    const { error: errorToast } = useToaster();
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<CatalogItemFilters>({});
    const [archivedFilter, setArchivedFilter] =
        useState<CatalogItemArchivedFilter>(CatalogItemArchivedFilter.ACTIVE);
    // Default to Kick (native) so the first load is Kick-only; clearing the
    // selection widens the list to every account.
    const [sources, setSources] = useState<InvoiceSource[]>(
        DEFAULT_CATALOG_ITEM_ACCOUNT_SOURCES,
    );
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    // editingItem is kept set while the edit modal animates closed (only
    // isEditModalOpen flips), so the form content doesn't blank mid-animation.
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<CatalogItemRow | null>(null);
    const [page, setPage] = useState(1);

    const businessEntities = useEntities({ onlyBusiness: true });
    const { data: chartOfAccounts } = useChartOfAccountsQuery();
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const hasAnyAccrualLedger = useIsAccrualLedgerEnabled();
    const showReceivablesAccount =
        hasAccrualLedgerAccess && hasAnyAccrualLedger;
    const viewConfig = useMemo(
        () =>
            createCatalogItemViewConfig({
                hasClassesAccess,
                showReceivablesAccount,
            }),
        [hasClassesAccess, showReceivablesAccount],
    );
    const defaultView = useMemo(
        () => buildDefaultView(hasClassesAccess),
        [hasClassesAccess],
    );

    // Mirror the EntityFilterCombobox "all selected" state: when every business
    // entity is selected (or nothing is), scope is the whole workspace, so we
    // send no entity filter. Only a strict subset filters the items list.
    const selectedEntityIds = useMemo(
        () => filters.entityIds ?? [],
        [filters.entityIds],
    );
    const isAllEntitiesSelected =
        selectedEntityIds.length === 0 ||
        (businessEntities.length > 0 &&
            businessEntities.every((entity) =>
                selectedEntityIds.includes(entity.id),
            ));
    const entityIds = isAllEntitiesSelected ? undefined : selectedEntityIds;
    // Class filtering happens on the backend. "No classes" needs no selection;
    // the other modes only apply once classes are picked, so a mode left over
    // from an emptied selection is not sent.
    const classIds = filters.classes?.length ? filters.classes : undefined;
    const classFilterType = resolveCatalogItemClassFilterType({
        classFilterType: filters.classFilterType,
        classIds,
    });
    // Receivables account filtering is gated like its column: a stale selection
    // is not sent once the workspace loses accrual ledger access.
    const incomeAccountIds = filters.incomeAccountIds?.length
        ? filters.incomeAccountIds
        : undefined;
    const receivablesAccountIds =
        showReceivablesAccount && filters.receivablesAccountIds?.length
            ? filters.receivablesAccountIds
            : undefined;

    const [cachedView, setCachedView] = useWorkspaceUserCache<CatalogItemView>(
        WORKSPACE_USER_CACHE_KEYS.ITEMS_PAGE_VIEW_CACHE_KEY,
        CatalogItemViewSchema,
    );

    const [view, setView] = useState<CatalogItemView>(
        cachedView ?? defaultView,
    );

    // A persisted/cached view can still carry the Class or Receivables
    // account columns after a workspace loses the corresponding plan access
    // (or has no accrual ledger).
    // buildColumns renders every column in view.columns regardless of the
    // `internal` flag, so strip the gated columns from the active view too —
    // not just from Column Settings. Income account stays — it is copied
    // onto invoice line / transaction coding for every plan.
    const gatedColumnIds = useMemo(
        () =>
            getGatedCatalogItemColumns({
                hasClassesAccess,
                showReceivablesAccount,
            }),
        [hasClassesAccess, showReceivablesAccount],
    );
    const effectiveView = useMemo(
        () => stripGatedCatalogItemColumns(view, gatedColumnIds),
        [view, gatedColumnIds],
    );

    const viewWithBulkActions = useMemo<CatalogItemView>(
        () => ({
            ...effectiveView,
            options: {
                ...effectiveView.options,
                ...BULK_SELECTION_VIEW_OPTIONS,
            },
        }),
        [effectiveView],
    );

    const sort = view.sort ?? DEFAULT_CATALOG_ITEM_SORT;

    // Reset to page 1 whenever a filter/sort/search input narrows or reorders
    // the matching set, so the user doesn't land on a now out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [
        search,
        entityIds,
        archivedFilter,
        sort,
        sources,
        classIds,
        classFilterType,
        incomeAccountIds,
        receivablesAccountIds,
    ]);

    const {
        items,
        total,
        pageCount,
        isLoading,
        isFetching,
        isPlaceholderData,
    } = useCatalogItems({
        search: search || undefined,
        entityIds,
        archivedFilter,
        sort,
        sources: sources.length > 0 ? sources : undefined,
        classIds,
        classFilterType,
        incomeAccountIds,
        receivablesAccountIds,
        page,
        limit: ITEMS_PAGE_SIZE,
    });

    // Clamp the current page back into range when the result set shrinks
    // (e.g. archiving/deleting the last item on the final page lowers
    // pageCount), so the user isn't stranded on an empty out-of-range page.
    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const updateCatalogItem = useUpdateCatalogItem();
    const deleteCatalogItem = useDeleteCatalogItem();
    const duplicateCatalogItem = useDuplicateCatalogItem();

    const rows = useMemo<CatalogItemRow[]>(
        () => buildCatalogItemRows(items),
        [items],
    );

    const { rowSelection, setRowSelection, selectedRows, selectOnly } =
        useCatalogItemRowSelection(rows);
    const clearSelection = useCallback(() => selectOnly([]), [selectOnly]);

    const { handleOpenBulkActions, isBulkActionDisabled } =
        useCatalogItemBulkActions({
            selectedRows,
            selectOnly,
            hasClassesAccess,
            hasAccrualLedgerAccess,
            isSelectionStale: isPlaceholderData,
        });

    const callbacks = useMemo<CatalogItemsTableCallbacks>(
        () => ({
            onEdit: (item) => {
                setEditingItem(item);
                setIsEditModalOpen(true);
            },
            onDuplicate: async (item) => {
                try {
                    await duplicateCatalogItem.mutateAsync(item.id);
                } catch (err) {
                    // The axios interceptor only toasts when the response
                    // carries a `data.message`. Cover every gap it can't (network
                    // failures with no response, or an HTTP error whose body has
                    // no message, e.g. a proxy 502/503 with an HTML body) so the
                    // action never fails completely silently.
                    if (!isAxiosError(err) || !err.response?.data?.message) {
                        errorToast(`Failed to duplicate ${item.name}`);
                    }
                }
            },
            onArchiveToggle: async (item) => {
                try {
                    await updateCatalogItem.mutateAsync({
                        catalogItemId: item.id,
                        body: { active: !item.active },
                    });
                } catch (err) {
                    if (!isAxiosError(err) || !err.response?.data?.message) {
                        errorToast(
                            `Failed to ${item.active ? "archive" : "enable"} ${item.name}`,
                        );
                    }
                }
            },
            onDelete: async (item) => {
                // The API client surfaces the backend error message (e.g. the
                // 409 "...archive it instead" for referenced items), so we
                // intentionally don't toast a second, hard-coded message when a
                // `data.message` is present. Cover every gap it can't (network
                // failures with no response, or an HTTP error whose body has no
                // message) so a failed delete never goes unreported.
                try {
                    await deleteCatalogItem.mutateAsync({
                        catalogItemId: item.id,
                    });
                } catch (err) {
                    if (!isAxiosError(err) || !err.response?.data?.message) {
                        errorToast(`Failed to delete ${item.name}`);
                    }
                }
            },
        }),
        [
            updateCatalogItem,
            deleteCatalogItem,
            duplicateCatalogItem,
            errorToast,
        ],
    );

    const tableMeta = useMemo<CatalogItemsTableMeta>(
        () => ({ callbacks, options: { selectedEntityIds: entityIds } }),
        [callbacks, entityIds],
    );

    const handleViewChange = useCallback(
        (newView: View) => {
            const { selection, bulkActions, ...persistedOptions } =
                newView.options ?? {};
            const result = CatalogItemViewSchema.safeParse({
                ...newView,
                options: persistedOptions,
            });
            const parsedView = result.success
                ? restoreGatedCatalogItemColumns({
                      previousView: view,
                      nextView: result.data,
                      gatedColumnIds,
                  })
                : defaultView;
            setView(parsedView);
            setCachedView(parsedView);
        },
        [setCachedView, defaultView, view, gatedColumnIds],
    );

    const handleSortChange = useCallback(
        (newSort: CatalogItemSort) => {
            const nextView: CatalogItemView = { ...view, sort: newSort };
            handleViewChange(nextView);
        },
        [handleViewChange, view],
    );

    const handleAddItem = useCallback(() => {
        setIsAddModalOpen(true);
    }, []);

    const hasActiveFilters = useMemo(() => {
        const hasSearch = search.trim().length > 0;
        const hasEntitySubset = !isAllEntitiesSelected;
        const hasClassFilter =
            classIds !== undefined ||
            classFilterType === ClassFilterType.NO_CLASSES;
        const hasAccountFilter =
            incomeAccountIds !== undefined ||
            receivablesAccountIds !== undefined;
        const hasNonDefaultArchived =
            archivedFilter !== CatalogItemArchivedFilter.ACTIVE;
        const hasNonDefaultSources =
            sources.length !== DEFAULT_CATALOG_ITEM_ACCOUNT_SOURCES.length ||
            !DEFAULT_CATALOG_ITEM_ACCOUNT_SOURCES.every((source) =>
                sources.includes(source),
            );

        return (
            hasSearch ||
            hasEntitySubset ||
            hasClassFilter ||
            hasAccountFilter ||
            hasNonDefaultArchived ||
            hasNonDefaultSources
        );
    }, [
        search,
        isAllEntitiesSelected,
        classIds,
        classFilterType,
        incomeAccountIds,
        receivablesAccountIds,
        archivedFilter,
        sources,
    ]);

    // Account ids are entity-scoped, so narrowing the entity scope drops the
    // selected accounts that no longer appear in the account tree; otherwise
    // they would keep filtering the list with no visible selection.
    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (newFilters) =>
            setFilters((prev) => {
                const next = { ...prev, ...newFilters };
                if (!("entityIds" in newFilters)) {
                    return next;
                }
                const accounts = chartOfAccounts?.accounts;
                return {
                    ...next,
                    incomeAccountIds: scopeAccountIdsToEntities({
                        accountIds: next.incomeAccountIds,
                        accounts,
                        entityIds: next.entityIds,
                    }),
                    receivablesAccountIds: scopeAccountIdsToEntities({
                        accountIds: next.receivablesAccountIds,
                        accounts,
                        entityIds: next.entityIds,
                    }),
                };
            }),
        [chartOfAccounts?.accounts],
    );

    const handleClearFilters = useCallback(() => {
        setSearch("");
        setFilters({});
        setArchivedFilter(CatalogItemArchivedFilter.ACTIVE);
        setSources(DEFAULT_CATALOG_ITEM_ACCOUNT_SOURCES);
    }, []);

    const isEmpty = !isLoading && !isFetching && total === 0;
    const emptyVariant = hasActiveFilters ? "filtered" : "create";
    const shouldHideFooter = isEmpty && emptyVariant === "create";

    const emptyView = useCallback(
        () => (
            <CatalogItemsEmptyView
                variant={emptyVariant}
                onClearFilters={handleClearFilters}
                onAddItem={handleAddItem}
            />
        ),
        [emptyVariant, handleClearFilters, handleAddItem],
    );

    const footer = useMemo(
        () =>
            shouldHideFooter ? undefined : (
                <CatalogItemsListFooter
                    total={total}
                    currentPage={page}
                    totalPages={pageCount}
                    onPageChange={setPage}
                    isLoading={isFetching}
                    selectedCount={selectedRows.length}
                    onClearSelection={clearSelection}
                    onOpenBulkActions={handleOpenBulkActions}
                    isBulkActionDisabled={isBulkActionDisabled}
                />
            ),
        [
            shouldHideFooter,
            total,
            page,
            pageCount,
            isFetching,
            selectedRows.length,
            clearSelection,
            handleOpenBulkActions,
            isBulkActionDisabled,
        ],
    );

    return (
        <main>
            <CatalogItemsListHeader
                search={search}
                setSearch={setSearch}
                filters={filters}
                showReceivablesAccountFilter={showReceivablesAccount}
                onFiltersChange={handleFiltersChange}
                archivedFilter={archivedFilter}
                onArchivedFilterChange={setArchivedFilter}
                sources={sources}
                onSourcesChange={setSources}
                onAddItem={handleAddItem}
                view={effectiveView}
                onViewChange={handleViewChange}
                viewConfig={viewConfig}
                defaultView={defaultView}
            />
            <ViewTable
                data={isLoading && rows.length === 0 ? null : rows}
                view={viewWithBulkActions}
                config={viewConfig}
                sort={sort}
                onSortChange={handleSortChange}
                onViewChange={handleViewChange}
                rowBorders={false}
                isParentSelectable={false}
                fixedTopOffsetPx
                tableMeta={tableMeta}
                emptyView={emptyView}
                footer={footer}
                rowSelection={rowSelection}
                enableKeyboardShortcuts
                onRowSelectionChange={setRowSelection}
            />
            <CatalogItemFormModal
                show={isAddModalOpen}
                onHide={() => setIsAddModalOpen(false)}
            />
            <CatalogItemFormModal
                show={isEditModalOpen}
                item={editingItem}
                onHide={() => setIsEditModalOpen(false)}
            />
        </main>
    );
};
